import type { Conf, UserEvent } from './global';
import monitor from './tracker';
import { trackMixpanel } from './tracker';

export const WEBWIDGET_SELECTOR = '#webWidget';
export const CHAT_SELECTOR = 'li[data-pendo-notification-id=chat]';
export const CHAT_SUB_DIV = `${CHAT_SELECTOR} div`;
export const CHAT_TITLE = `${CHAT_SELECTOR} ._pendo-resource-center-module-list-item-title-text`;
export const CHAT_SUBTITLE =
    CHAT_SELECTOR + ' ._pendo-resource-center-module-list-item-subtitle-text';

export const getZe = () => {
    if (typeof window.zE == 'function') {
        return window.zE;
    }
    throw new Error('zE is not defined');
};

export const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    return parts.pop()?.split(';').shift() || undefined;
};

export const getDataSet = (key: string): string | undefined => {
    const script = document.getElementById('zendesk_chattag');
    return script?.getAttribute(`data-${key}`) || undefined;
};

export const isDataType = (key: string): boolean => getDataSet('type') === key || false;

export const isSalesArticle = (conf: Conf) =>
    isSales(conf) &&
    window.location.pathname.includes(
        'Changes-to-free-Basic-plan-Limitation-on-the-amount-of-published-documents',
    );

export const waitCondition = (
    conditionFn: () => boolean,
    error: Error,
    count = 20,
    interval = 300,
): Promise<void> =>
    new Promise((resolve, reject) => {
        const waitLogout = () => {
            if (count-- < 0) {
                reject(error);
            } else if (conditionFn()) {
                resolve();
            } else {
                setTimeout(waitLogout, interval);
            }
        };
        waitLogout();
    });

export class WaitTimeout extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WaitTimeOut';
    }
}

const getLogOutBtn = (): HTMLLinkElement | null => {
    return document.querySelector(
        "a[href*='logout'], a[data-track='logout'], #marketing-header-signin-button",
    );
};

const hasLogOut = (): boolean => !!getLogOutBtn();

const hasZe = (): boolean =>
    typeof window.zE === 'function' && typeof window.zE.hide === 'function';

const setLogOutListener = (): boolean => {
    const logoutBtn = getLogOutBtn();

    if (!logoutBtn) {
        return false;
    }
    logoutBtn.addEventListener('click', event => {
        event.preventDefault();
        window.zE?.logout();
        window.location.href = logoutBtn.href || '/';
    });
    return true;
};

// Check element existence by MutationObserver
export const checkExist = (
    parentSel: HTMLElement | string,
    targetSel: string,
    callback: (observer: MutationObserver, conf: Conf) => void,
    conf: Conf,
) => {
    const parentEl = typeof parentSel === 'string' ? document.querySelector(parentSel) : parentSel;
    if (!parentEl) {
        return;
    }
    const observer = new MutationObserver(l => {
        l.forEach(m => {
            m.addedNodes.forEach(e => {
                const targetEl = e.parentElement?.querySelector(targetSel);
                if (targetEl) {
                    callback(observer, conf);
                }
            });
        });
    });
    const mapKey = targetSel;
    conf.observerMap[mapKey] && conf.observerMap[mapKey].disconnect();
    conf.observerMap[mapKey] = observer;
    observer.observe(parentEl, {
        subtree: true,
        childList: true,
    });
};

const setIdentity = (conf: Conf) => {
    if (!conf.email || !conf.username) {
        return;
    }
    window.zE?.identify({
        name: conf.username,
        email: conf.email,
    });
};

export const waitZe = async (callbackFn: (conf: Conf) => void, conf: Conf) => {
    await waitCondition(hasZe, conf.waitError(`Wait ${conf.tagType} zE timeout`));
    if (conf.checkLogout) {
        await waitCondition(hasLogOut, conf.waitError(`Wait ${conf.tagType} logoutBtn timeout`));
        setLogOutListener();
    }
    callbackFn(conf);
};

export const disableChat = () => {
    setTimeout(() => {
        getZe()('webWidget', 'updateSettings', {
            webWidget: { chat: { suppress: true } },
        });
    }, 0);
};

export const enableChat = (conf: Conf) => {
    getZe()('webWidget', 'updateSettings', {
        webWidget: {
            chat: { suppress: false, departments: { enabled: [], select: conf.department } },
        },
    });
    setIdentity(conf);
};

export const setPendoChatDisplay = (display: string) => {
    let style = document.querySelector('#pendo-chat-style');
    if (style) {
        style.remove();
    }
    style = document.createElement('style');
    style.id = 'pendo-chat-style';
    style.textContent = `${CHAT_SELECTOR} { display: ${display}; }`;
    document.head.appendChild(style);
};

export const addTags = (tags: string[], conf: Conf) => {
    const newTags = tags.concat([`plan_${conf.plan}`]);
    if (conf.isTeamUser) {
        newTags.push('team_user');
    }
    getZe()('webWidget', 'chat:addTags', newTags);
};

export const setSalesText = () => {
    getZe()('webWidget', 'updateSettings', {
        webWidget: {
            chat: {
                concierge: { name: 'Live Sales', title: { '*': 'Sales Help' } },
            },
            launcher: {
                label: { '*': 'Sales' },
                chatLabel: { '*': 'Chat with Sales' },
            },
        },
    });
};

export const setupHelpCenterTitle = () => {
    getZe()('webWidget', 'updateSettings', {
        webWidget: {
            helpCenter: {
                title: { '*': 'Help Center' },
            },
        },
    });
};

type Features = 'answerBot' | 'talk' | 'helpCenter' | 'contactForm' | 'chat';
export const handleFeaturesSupression = (disables: Features[], enables?: Features[]) => {
    const disabled = disables.reduce((acc, cur) => ({ ...acc, [cur]: { suppress: true } }), {});
    const enabled = enables?.reduce((acc, cur) => ({ ...acc, [cur]: { suppress: false } }), {});
    getZe()('webWidget', 'updateSettings', {
        webWidget: { ...disabled, ...enabled },
    });
};

export const monitorUserEvent = (conf: Conf) => {
    getZe()('webWidget:on', 'userEvent', (event: UserEvent) => {
        const { tagType, email, username, isPaidUser, isTeamUser } = conf;
        const eventAction = event.action.toLowerCase().replace(/\s/g, '_');
        const action = `${tagType}.${eventAction}`;
        const isSales = conf.department === 'Sales';
        const isSupport = conf.department === 'Support';

        const data = {
            action,
            email: email || 'unknown_email',
            username: username || 'Anonymous',
            isPaidUser,
            isSales,
            isSupport,
            isTeamUser,
            isAnonymous: !username,
        };
        trackMixpanel(`zendesk-chattag`, data);

        monitor(data);
    });
};

export const isSales = (conf: Conf) => conf.department === 'Sales';

export const isDev = () => window.location.href.includes('tissuu.com');
